// frontend/src/components/DepartmentDashboard.js
//
// The dashboard shell for a department (/departments/:code). Today only Sales has
// comp plans, and those live on My Dashboard / Team View. For every other
// department this page is the scaffold its commissions will grow into: who is in
// the department, and the plan status. Access is enforced by the API (the
// caller's department, or Operations / Finance, or global_admin) and mirrored by
// the route guard in App.js.
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { getDepartment } from '../api/client';
import { useAuth } from '../App';
import OperationsPlanDashboard from './OperationsPlanDashboard';
import FinanceDepartmentDashboard from './FinanceDepartmentDashboard';
import PresalesDashboard from './PresalesDashboard';
import PseDashboard from './PseDashboard';
import { visibleDepartmentsFor } from '../App';

const ROLE_LABEL = { rep: 'Rep', manager: 'Manager', finance_admin: 'Finance Admin', global_admin: 'Global Admin' };

export default function DepartmentDashboard() {
  const { code } = useParams();
  const { user } = useAuth();
  const { data, loading, error } = useApi(() => getDepartment(code), [code]);

  if (loading) {
    return <div style={{ padding: '48px', textAlign: 'center' }}><div className="loader-ring" style={{ margin: '0 auto' }} /></div>;
  }
  if (error || !data) {
    return (
      <div className="page-enter page-wrap" style={{ maxWidth: '1200px' }}>
        <div className="card" style={{ padding: '24px' }}>
          <h3 style={{ marginBottom: '6px' }}>Department unavailable</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>{(typeof error === 'string' ? error : error?.message) || 'Could not load this department.'}</p>
        </div>
      </div>
    );
  }

  const { label, configured, plans, members, counts, isMine } = data;
  // Operations has a live comp plan — its dashboard replaces the scaffold.
  if (code === 'operations') return <OperationsPlanDashboard members={members} label={label} isMine={isMine} />;
  // Finance: total commissions by department (monthly actuals + quarterly accruals).
  if (code === 'finance') return <FinanceDepartmentDashboard members={members} label={label} isMine={isMine} />;
  // Pre-Sales: members see only their own plan (the "PSE View"); finance roles and
  // cross-department viewers get the department view with both plans + settings.
  if (code === 'presales') {
    const privileged = ['finance_admin', 'global_admin'].includes(user?.app_role) || visibleDepartmentsFor(user).length > 1;
    if (user?.department === 'presales' && !privileged) return <PseDashboard />;
    return <PresalesDashboard members={members} label={label} isMine={isMine} />;
  }
  const isSales = code === 'sales';
  const canSeeSalesPages = ['manager', 'finance_admin', 'global_admin'].includes(user?.app_role);

  return (
    <div className="page-enter page-wrap" style={{ maxWidth: '1200px' }}>
      <div className="page-header">
        <div>
          <div className="label" style={{ marginBottom: '6px' }}>Department{isMine ? ' · yours' : ''}</div>
          <h1 style={{ fontSize: '1.6rem' }}>{label}</h1>
        </div>
      </div>

      {/* ── Status tiles ── */}
      <div className="kpi-grid-4" style={{ gap: '12px', marginBottom: '20px' }}>
        <Tile label="Members" value={counts.members} />
        <Tile label="Active" value={counts.active} accent="green" />
        <Tile label="Comp plans" value={configured ? plans.length : 'Not configured'} accent={configured ? 'blue' : 'muted'} small={!configured} />
        <Tile label="Commission basis" value={isSales ? 'Fulfillment' : '—'} accent="blue" small />
      </div>

      {/* ── Plan status ── */}
      <div className="card" style={{ padding: '20px 24px', marginBottom: '20px', borderColor: configured ? 'var(--border-bright)' : undefined }}>
        {configured ? (
          <>
            <div className="label" style={{ marginBottom: '8px' }}>Comp plans in effect</div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
              {plans.map(p => <span key={p} className="badge badge-approved">{p}</span>)}
            </div>
            {isSales && (
              <p style={{ fontSize: '13.5px', color: 'var(--text-secondary)', lineHeight: 1.55, margin: 0 }}>
                Sales commissions are calculated from shipped gross profit on closed-won opportunities.
                Reps see their own earnings on <strong>My Dashboard</strong>
                {canSeeSalesPages && <>; managers and finance see everyone on <Link to="/team">Team View</Link></>}.
              </p>
            )}
          </>
        ) : (
          <>
            <div className="label" style={{ marginBottom: '8px' }}>No comp plan configured yet</div>
            <p style={{ fontSize: '13.5px', color: 'var(--text-secondary)', lineHeight: 1.55, margin: 0 }}>
              {label} is set up as a department, but its commission plan has not been defined.
              Once it is, this page becomes the {label} dashboard: earnings, payouts, and the
              math behind them, the same way Sales works today. Until then, nothing is paid
              through the app for {label}.
            </p>
          </>
        )}
      </div>

      {/* ── Members ── */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)' }}>
          <h3>{label} team ({counts.members})</h3>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>
            Department membership is set on Finance → Users.
          </p>
        </div>
        {members.length === 0 ? (
          <div style={{ padding: '32px 24px', color: 'var(--text-muted)', fontSize: '14px' }}>
            No one is assigned to {label} yet.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {members.map(m => (
                  <tr key={m.id} style={{ opacity: m.active ? 1 : 0.55 }}>
                    <td style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{m.display_name || '—'}</td>
                    <td style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{m.email || '—'}</td>
                    <td>{ROLE_LABEL[m.app_role] || m.app_role}</td>
                    <td>
                      <span className={`badge ${m.active ? 'badge-paid' : 'badge-disputed'}`}>
                        {m.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Tile({ label, value, accent = 'blue', small = false }) {
  const color = accent === 'green' ? 'var(--green)' : accent === 'muted' ? 'var(--text-muted)' : 'var(--blue-primary)';
  return (
    <div className="card" style={{ padding: '16px 18px' }}>
      <div className="label" style={{ marginBottom: '8px' }}>{label}</div>
      <div style={{ fontSize: small ? '15px' : '24px', fontWeight: 700, color, fontFamily: small ? 'var(--font-ui)' : 'var(--font-mono)' }}>
        {value}
      </div>
    </div>
  );
}
